import { PayloadAction } from '@reduxjs/toolkit';
import { put, takeLatest, delay, all, select } from 'redux-saga/effects';
import { globalActions } from 'app/containers/Global/slice';
import culturesApi from '../culturesApi';
import { culturesActions as actions } from '.';
import {
  ICulture,
  ICompanyCulture,
  ICreateOrEditCulturePayload,
  IGetCultureDetailsPayload,
} from './types';
import { RouteConstants } from 'app/routes';
import i18next from 'i18next';
import { notification } from 'antd';
import { sortCultureList } from './helpers';
import { selectDeletedCulturePolygonsIds } from 'app/containers/Map/slice/selectors';
import { generatePath } from 'react-router-dom';

function* getCulturesSaga() {
  try {
    const cultures: ICulture[] = yield culturesApi.getCultures();

    yield put(actions.setCultures(cultures));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(actions.setCultures([]));
  } finally {
    yield put(actions.setCulturesLoading(false));
  }
}

function* getCulturesListSaga(
  action: PayloadAction<{
    hiikoEnvironmentId: number;
    cultureId: number | null;
    seasonId: number;
  }>,
) {
  try {
    const { hiikoEnvironmentId, cultureId, seasonId } = action.payload;

    const cultures: ICompanyCulture[] = yield culturesApi.getCulturesList({
      seasonId,
      hiikoEnvironmentId,
      cultureId,
    });

    const sortedCultureList = sortCultureList(cultures);
    yield put(actions.setCulturesList(sortedCultureList));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(
      actions.setCulturesList({
        cultures: [],
        companies: [],
      }),
    );
  } finally {
    yield put(actions.setCulturesListLoading(false));
  }
}

function* getCultureDetailsSaga(
  action: PayloadAction<IGetCultureDetailsPayload>,
) {
  try {
    const {
      hiikoEnvironmentId,
      cultureId,
      companyId,
      seasonId,
    } = action.payload;

    let culturesArr: ICompanyCulture[] = [];
    let selectedCompanyId = null;

    if (companyId) {
      // Get 1 company with the culture
      const companyCulture:
        | ICompanyCulture
        | undefined = yield culturesApi.getCompanyCultureById({
        hiikoEnvironmentId,
        cultureId,
        companyId,
        seasonId,
      });
      selectedCompanyId = companyId;
      if (companyCulture) {
        culturesArr = [companyCulture];
      }
    } else {
      // Get all companies with the culture
      culturesArr = yield culturesApi.getCulturesList({
        hiikoEnvironmentId,
        cultureId,
        seasonId,
      });
    }

    yield put(
      actions.setCultureDetails({ culturesArr, companyId: selectedCompanyId }),
    );
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setCultureDetailsLoading(false));
  }
}

function* createOrEditCultureSaga(
  action: PayloadAction<ICreateOrEditCulturePayload>,
) {
  try {
    const {
      isEdit,
      hiikoEnvironmentId,
      cultureId,
      selectedFields,
      harvests,
      companyId: pageCompanyId,
    } = action.payload;

    if (!cultureId) {
      notification.error({
        message: i18next?.t(
          'Cultures.CreateCulture.Error.SelectCulture',
          'Culture must be selected',
        ),
        placement: 'bottomRight',
      });
    }

    if (selectedFields.length <= 0) {
      return notification.error({
        message: i18next?.t(
          'Cultures.CreateCulture.Error.SelectFields',
          'At least one field must be selected',
        ),
        placement: 'bottomRight',
      });
    }
    const deletedFieldsIds: number[] = yield select(
      selectDeletedCulturePolygonsIds,
    );

    if (deletedFieldsIds.length) {
      yield culturesApi.removeCultureFromField(
        hiikoEnvironmentId,
        deletedFieldsIds,
      );
    }

    yield all(
      selectedFields.map(field => {
        const companyId = field.companyId;

        const data = {
          companyId,
          cultureId,
          fieldSeasonIds: field.fieldSeasonIds,
          harvest: harvests[companyId] || 0,
        };

        return culturesApi.createOrUpdateCompanyCulture(
          hiikoEnvironmentId,
          data,
        );
      }),
    );

    let redirectUrl = generatePath(RouteConstants.cultures.list, {
      envId: hiikoEnvironmentId,
    });
    if (isEdit) {
      if (pageCompanyId) {
        redirectUrl = generatePath(RouteConstants.cultures.detailsByCompany, {
          envId: hiikoEnvironmentId,
          cultureId,
          companyId: pageCompanyId,
        });
      } else {
        redirectUrl = generatePath(RouteConstants.cultures.details, {
          envId: hiikoEnvironmentId,
          cultureId,
        });
      }
    }
    yield put(globalActions.setRedirectTo(redirectUrl));

    yield delay(500);

    notification.success({
      message: isEdit
        ? i18next?.t(
            'Cultures.CreateCulture.Edited',
            'Culture updated successfully',
          )
        : i18next?.t(
            'Cultures.CreateCulture.Created',
            'Culture created successfully',
          ),
      description: isEdit
        ? ''
        : i18next?.t(
            'Cultures.CreateCulture.CreatedDescription',
            'You can find the culture in the culture list. You can modify it in the culture window.',
          ),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setCreateOrEditCultureLoading(false));
  }
}

export function* culturesSaga() {
  yield takeLatest(actions.getCultures.type, getCulturesSaga);
  yield takeLatest(actions.getCulturesList.type, getCulturesListSaga);
  yield takeLatest(actions.getCultureDetails.type, getCultureDetailsSaga);
  yield takeLatest(actions.createOrEditCulture.type, createOrEditCultureSaga);
}
